/**
 * QuestionChart — renders a distribution three ways (bar / donut / line).
 *
 * The chart type is chosen by the user via the toggle in QuestionResultCard. Guidance
 * from the survey-viz research: BAR is the best default for comparing options; a
 * DONUT reads composition (part-of-whole); a LINE (frequency polygon over the ordered
 * options) shows the shape/skew of an ordinal scale. Bars use the branded row style;
 * donut + line use Victory (already a dependency).
 *
 * Labels:
 * - Donut: each slice carries its COUNT, centred in the ring band (labelRadius sits
 *   between inner/outer radius so numbers never drift onto slice edges), plus a legend
 *   below. Hovering a slice shows a branded tooltip with the option name + count + %.
 * - Line: options are named on the x-axis; hovering a point shows a branded tooltip
 *   with the option name + count (a Voronoi overlay makes the whole column hoverable).
 */
import React, { FC } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
    VictoryAxis,
    VictoryChart,
    VictoryContainer,
    VictoryLine,
    VictoryPie,
    VictoryScatter,
    VictoryTheme,
    VictoryTooltip,
    VictoryVoronoiContainer,
} from "victory";
import { verbalTheme } from "../../layout/themes/verbal.theme";
import type { DistributionRow } from "./aggregate";
import { DistributionBar } from "./DistributionBar";

export type ChartType = "bar" | "pie" | "line";

type QuestionChartProps = {
    rows: DistributionRow[];
    chartType: ChartType;
    /** One colour per row (parallel to `rows`); cycled if shorter. */
    colors: string[];
    /** Respondents answering — denominator for the percentages. */
    answered: number;
};

const fontFamily = verbalTheme.typography.fontFamily;
const colorAt = (colors: string[], i: number): string => colors[i % colors.length];
const pctOf = (count: number, answered: number): number => (answered ? Math.round((count / answered) * 100) : 0);

/** Perceived brightness of a #rrggbb colour (0–255). */
const brightness = (hex: string): number => {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
    if (!m) return 0;
    const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h, 16));
    return 0.299 * r + 0.587 * g + 0.114 * b;
};

/** Readable label colour for text sitting on a coloured slice. */
const labelOn = (hex: string): string =>
    brightness(hex) > 150 ? verbalTheme.palette.secondary.main : verbalTheme.palette.common.white;

// Donut geometry — kept in sync between the visible pie and the invisible hover
// overlay so their slices align exactly. Outer radius for a 300×240 / padding-16
// frame is ~104; inner is 68, so the mid-band (where counts sit) is ~86.
const DONUT = { width: 300, height: 240, padding: 16, innerRadius: 68, labelRadius: 86 };

/** Branded flyout for Victory tooltips (navy card, white text). */
const brandedTooltip = (
    <VictoryTooltip
        constrainToVisibleArea
        cornerRadius={6}
        flyoutPadding={{ top: 6, bottom: 6, left: 10, right: 10 }}
        pointerLength={6}
        style={{ fontSize: 11, fontFamily, fill: verbalTheme.palette.common.white }}
        flyoutStyle={{ fill: verbalTheme.palette.secondary.main, stroke: "none" }}
    />
);

export const QuestionChart: FC<QuestionChartProps> = ({ rows, chartType, colors, answered }) => {
    if (chartType === "bar") {
        return (
            <Box>
                {rows.map((row, i) => (
                    <DistributionBar
                        key={`${row.value}-${row.label}`}
                        label={row.label}
                        count={row.count}
                        pct={answered ? row.count / answered : 0}
                        color={colorAt(colors, i)}
                    />
                ))}
            </Box>
        );
    }

    if (chartType === "pie") {
        const slices = rows.filter((r) => r.count > 0);
        // Keep each slice's colour aligned with its row index so it matches the other views.
        const sliceColour = (i: number): string => colorAt(colors, rows.indexOf(slices[i]));
        const pieData = slices.map((r) => ({ x: r.label, y: r.count }));
        // Per-slice label colour keyed by its option label — white on dark slices, navy on light.
        const labelFill: Record<string, string> = {};
        slices.forEach((r, i) => {
            labelFill[r.label] = labelOn(sliceColour(i));
        });
        const tooltipLabel = ({ datum }: { datum: { x: string; y: number } }): string =>
            `${datum.x}: ${datum.y} (${pctOf(datum.y, answered)}%)`;
        // Victory resolves a datum-callback for `fill` at runtime, but its TS type only
        // admits string | number — cast the callback through so labels can be per-slice.
        const labelFillFn = (({ datum }: { datum: { x: string } }) => labelFill[datum.x]) as unknown as string;

        return (
            <Box>
                <Box sx={{ position: "relative", width: "100%", maxWidth: DONUT.width, mx: "auto" }}>
                    {/* Visible layer: coloured slices + centred count labels. */}
                    <VictoryPie
                        data={pieData}
                        colorScale={slices.map((_, i) => sliceColour(i))}
                        innerRadius={DONUT.innerRadius}
                        padAngle={1}
                        labels={({ datum }) => String(datum.y)}
                        labelRadius={DONUT.labelRadius}
                        labelPlacement="vertical"
                        style={{
                            labels: {
                                fontSize: 13,
                                fontFamily,
                                fill: labelFillFn,
                                fontWeight: 600,
                            },
                        }}
                        width={DONUT.width}
                        height={DONUT.height}
                        padding={DONUT.padding}
                        containerComponent={<VictoryContainer responsive={true} />}
                    />
                    {/* Hover layer: identical geometry, transparent slices, branded tooltip. */}
                    <Box sx={{ position: "absolute", inset: 0 }}>
                        <VictoryPie
                            data={pieData}
                            innerRadius={DONUT.innerRadius}
                            padAngle={1}
                            labels={tooltipLabel}
                            labelComponent={brandedTooltip}
                            style={{ data: { fill: "transparent", stroke: "none" } }}
                            width={DONUT.width}
                            height={DONUT.height}
                            padding={DONUT.padding}
                            containerComponent={<VictoryContainer responsive={true} style={{ overflow: "visible" }} />}
                        />
                    </Box>
                </Box>
                <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1.5} mt={1}>
                    {slices.map((r, i) => (
                        <Box key={`${r.value}-${r.label}`} display="flex" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: sliceColour(i) }} />
                            <Typography variant="caption" color="textSecondary">
                                {r.label} ({r.count})
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>
        );
    }

    // line — frequency polygon over the ordered options (options named on the x-axis;
    // hovering a column shows the option name + count via the Voronoi overlay).
    const data = rows.map((r, i) => ({ x: i + 1, y: r.count }));
    const stroke = colorAt(colors, 0);
    return (
        <Box sx={{ width: "100%", maxWidth: 560 }}>
            <VictoryChart
                theme={VictoryTheme.material}
                width={480}
                height={260}
                domainPadding={{ x: 24, y: 16 }}
                padding={{ top: 16, bottom: 72, left: 44, right: 24 }}
                containerComponent={
                    <VictoryVoronoiContainer
                        responsive={true}
                        style={{ overflow: "visible" }}
                        voronoiDimension="x"
                        // Line + scatter share the same points; only read one so the
                        // tooltip doesn't render the label twice.
                        voronoiBlacklist={["points"]}
                        labels={({ datum }: { datum: { x: number; y: number } }) =>
                            `${rows[datum.x - 1]?.label ?? ""}: ${datum.y}`
                        }
                        labelComponent={brandedTooltip}
                    />
                }
            >
                <VictoryAxis
                    tickValues={data.map((d) => d.x)}
                    tickFormat={(t: number) => rows[t - 1]?.label ?? ""}
                    style={{
                        tickLabels: { fontSize: 8, fontFamily, angle: -30, textAnchor: "end" },
                    }}
                />
                <VictoryAxis dependentAxis style={{ tickLabels: { fontSize: 9, fontFamily } }} />
                <VictoryLine data={data} interpolation="monotoneX" style={{ data: { stroke, strokeWidth: 2 } }} />
                <VictoryScatter name="points" data={data} size={4} style={{ data: { fill: stroke } }} />
            </VictoryChart>
        </Box>
    );
};
